import type { ActionConfiguration, WorkflowItem, WorkflowStage } from "@/types/workflow"
import { evaluateCondition, substituteFieldsInUrl } from "./external-source-utils"
import { sendMessageToParent } from "./app-messaging"

export interface ActionUserContext {
  userProfileTypeId?: string | null
  isInternalUser?: boolean
}

/**
 * Builds the evaluation context by merging the item with stage and user info.
 */
export function buildConditionContext(
  item: WorkflowItem,
  currentStage: WorkflowStage,
  userContext?: ActionUserContext
): WorkflowItem {
  return {
    ...item,
    Stage: currentStage,
    ...(userContext && {
      UserProfileTypeId: userContext.userProfileTypeId ?? null,
      IsInternalUser: userContext.isInternalUser ?? false,
    }),
  }
}

/**
 * Filters and returns available actions for a given item and stage
 */
export function getAvailableActions(
  actionConfigurations: ActionConfiguration[],
  currentStage: WorkflowStage,
  item: WorkflowItem,
  userContext?: ActionUserContext
): ActionConfiguration[] {
  if (!actionConfigurations || actionConfigurations.length === 0) {
    return []
  }

  const context = buildConditionContext(item, currentStage, userContext)

  return actionConfigurations.filter(action => {
    // Cell-link actions (columnCode set) are not menu items
    if (action.columnCode) {
      return false
    }

    // Condition always takes priority — even for isDefault actions
    if (action.condition) {
      return evaluateCondition(action.condition, context)
    }

    return true
  })
}

/**
 * Resolves the ID the stage-transition API expects for a row. THE single
 * source of truth — used both when SENDING (performStageTransition) and when
 * translating the API's echoed `itemId` values back to local row ids
 * (buildSentIdToLocalIdMap). If the two ever diverge, post-action optimistic
 * updates silently target ids no grid row has and the UI waits for the
 * refresh instead.
 *
 *   - Assignment projections → currentWorkflowHubReportId (the actual
 *     WorkOrder to update).
 *   - WorkOrder → id (WorkflowHubV2Report.id is the canonical doc id, not
 *     reshaped by the alignment work).
 *   - VmsRequest / ClientRequest → requestId (their search-index id is
 *     SHA256-derived; the backend expects the source Cosmos doc id and would
 *     404 on the index guid).
 *   - Anything else → id (back-compat fallback).
 */
export function resolveStageTransitionItemId(item: WorkflowItem): string | undefined {
  const docType = ((item.documentType || item.DocumentType) as string ?? '').toLowerCase();
  const reportId = item.currentWorkflowHubReportId || item.CurrentWorkflowHubReportId;
  const localId = (item.id || (item as any).Id || (item as any).ID) as string | undefined;
  const requestId = ((item as any).requestId || (item as any).RequestId) as string | undefined;

  if (docType === 'assignment' && reportId) return String(reportId);
  if (docType === 'workorder' && localId) return String(localId);
  if ((docType === 'vmsrequest' || docType === 'clientrequest') && requestId) return String(requestId);
  return localId ? String(localId) : undefined;
}

/**
 * Builds a map from the ID sent to the stage-transition API (see
 * resolveStageTransitionItemId) back to the item's own local-state `id`.
 * Use this to translate the `itemId` values returned by the API into the
 * IDs needed to remove/update rows in local state.
 */
export function buildSentIdToLocalIdMap(items: WorkflowItem[]): Map<string, string> {
  return new Map(
    items.map(item => {
      const localId = item.id || (item as any).Id;
      const sentId = resolveStageTransitionItemId(item) ?? localId;
      return [String(sentId), String(localId)];
    })
  );
}

/**
 * Executes an action with appropriate callbacks
 */
export function executeAction(
  action: ActionConfiguration,
  item: WorkflowItem,
  callbacks: {
    onHistory: (itemId: string, itemTitle: string) => void
    onExternalLink: (url: string) => void
    onStageTransition: (action: ActionConfiguration, item: WorkflowItem) => void
    onCustomModal?: (action: ActionConfiguration, item: WorkflowItem) => void
    onEmailPreview?: (emailId: string, itemTitle: string) => void
    /**
     * Navigation hook fired when the kebab triggers an `open_linked_request`
     * action — currently only used by the VmsEmail row to jump to its linked
     * VmsRequest. The host (vms/tab.tsx) responds by switching the VMS tab
     * to the Requests sub-tab and scoping the grid to the request id.
     */
    onOpenLinkedRequest?: (requestId: string, itemTitle: string) => void
    /**
     * Hook fired when the kebab triggers a `view_duplicates` action. The
     * grid scopes itself to the current row + every id in
     * <c>PossibleDuplicateOfRequestIds</c> so the reviewer can see the
     * full duplicate cluster in one place. <paramref name="duplicateIds"/>
     * is the array off the row (already filtered for non-empty); the
     * handler is responsible for adding the current row's id if it
     * wants the source row visible in the scoped view.
     */
    onViewDuplicates?: (
      currentRowId: string,
      duplicateIds: string[],
      itemTitle: string
    ) => void
  }
) {
  // Normalize whitespace before matching — at least one stored config has
  // been observed with a trailing space (e.g. "custom_modal "), which
  // silently fell through to default and made the kebab a no-op. Trim
  // defensively; downstream consumers don't expect the surrounding spaces.
  const actionType = (action.actionType || '').trim()
  switch (actionType) {
    case 'history':
      {
        // The audit-history (and automation-emails) endpoint addresses the source
        // Cosmos doc, not the search-index row. For every entity family that lives
        // in the shared gw-client-request-index (VmsRequest, ClientRequest,
        // AtsPlacementRequest), `item.id` is the SHA256-derived index guid that the
        // audit backend has no record of — the canonical doc id lives on `RequestId`.
        // Use `requestId` for those families so the lookup hits the right document.
        // The empty-docType case covers legacy ClientRequest rows that index with
        // DocumentType == null; it's still safe for WorkOrder/Assignment because those
        // rows carry no `requestId` (so the `&& requestId` guard falls back to id).
        // VmsEmail and other families keep using `item.id`.
        const docType = ((item.documentType || item.DocumentType) as string || '').toLowerCase()
        const requestId = (item as any).requestId || (item as any).RequestId
        const auditItemId = (docType === 'vmsrequest' || docType === 'clientrequest' || docType === 'atsplacementrequest' || docType === '') && requestId
          ? requestId
          : (item.id || '')
        callbacks.onHistory(
          auditItemId,
          item.workerFullName || 'Unknown'
        )
      }
      break

    case 'external_link':
      if (action.link) {
        const url = substituteFieldsInUrl(action.link, item)
        callbacks.onExternalLink(url)
      }
      break

    case 'navigate':
      // Hands navigation off to the parent window via postMessage. The
      // host app (FBO portal, Procom portal, etc.) intercepts NAVIGATE
      // messages and routes accordingly — same shape client-requests-grid
      // uses for "Start Pre-verification" / "View Pre-verification" /
      // "New Onboarding Request". This lets the action's Link carry a
      // RELATIVE route (e.g. "/next/mfe/sync/requests/onboarding/create?...")
      // without the microapp having to know the host's absolute base URL.
      //
      // Field tokens like ${id} in the configured Link are substituted
      // from the row before sending, so per-row context (the VMS doc id,
      // for instance) lands in the final route as a querystring.
      if (action.link) {
        const route = substituteFieldsInUrl(action.link, item)
        sendMessageToParent({ action: 'NAVIGATE', route })
      } else {
        console.warn('navigate action has no link configured', action)
      }
      break

    case 'stage_transition':
      callbacks.onStageTransition(action, item)
      break

    case 'custom_modal':
      if (callbacks.onCustomModal) {
        callbacks.onCustomModal(action, item)
      }
      break

    case 'open_email_preview':
      // The email id lives on different fields depending on the row's entity:
      // VmsRequest rows store the linked email under linkedEmailId; VmsEmail
      // rows ARE the email so we use the row's own id. Anything else is a
      // misconfiguration — the seed only puts this actionType on those two.
      if (callbacks.onEmailPreview) {
        const docType = (
          (item as any).documentType || (item as any).DocumentType || ''
        ).toString().toLowerCase()
        const emailId = docType === 'vmsemail'
          ? (item.id || '')
          : ((item as any).linkedEmailId || (item as any).LinkedEmailId || '')
        if (emailId) {
          callbacks.onEmailPreview(emailId, item.workerFullName || item.id || 'Email')
        } else {
          console.warn('open_email_preview: no email id resolvable from row', item)
        }
      }
      break

    case 'view_duplicates':
      // VmsRequest "View Duplicates" — scope the current grid to this
      // row + every id listed in PossibleDuplicateOfRequestIds. The
      // action's Condition already gates on the array being non-empty
      // so we shouldn't normally see an empty list here, but the
      // defensive warn matches the open_email_preview / open_linked_request
      // pattern in case the seed Condition is ever changed.
      if (callbacks.onViewDuplicates) {
        const rawIds = ((item as any).possibleDuplicateOfRequestIds
          || (item as any).PossibleDuplicateOfRequestIds
          || []) as unknown[]
        const duplicateIds = rawIds
          .map(x => (x ?? '').toString().trim())
          .filter(x => x.length > 0)
        if (duplicateIds.length > 0) {
          const currentRowId = (item.id || '').toString()
          const title = (item.workerFullName || '').toString().trim() || currentRowId || 'Request'
          callbacks.onViewDuplicates(currentRowId, duplicateIds, title)
        } else {
          console.warn('view_duplicates: PossibleDuplicateOfRequestIds was empty on row', item)
        }
      }
      break

    case 'open_linked_request':
      // VmsEmail row → linked VmsRequest navigation. The email row carries
      // the request id under linkedVmsRequestId; the kebab action's Condition
      // already gates on that field being non-null, so we shouldn't normally
      // see a missing id, but the defensive warn matches the open_email_preview
      // pattern for visibility if the seed is ever misconfigured.
      //
      // Label preference for the destination grid's "Showing: …" chip:
      //   1. ParsedExternalRequestId — the LLM-extracted external VMS
      //      request id (e.g. Flextrack SEID, Fieldglass SOW#). This is
      //      the most identifiable handle for "which request" — same
      //      thing the user would search for in the VMS portal.
      //   2. Subject — fall back to the email subject (descriptive but
      //      longer) when no external id was extracted.
      //   3. workerFullName — fallback when both are blank.
      //   4. literal 'Request' — final fallback. We intentionally avoid
      //      `item.id` here because that's a UUID and the whole point of
      //      this label is for the chip to be human-readable.
      if (callbacks.onOpenLinkedRequest) {
        const requestId = (
          (item as any).linkedVmsRequestId
          || (item as any).LinkedVmsRequestId
          || ''
        ).toString()
        if (requestId) {
          const externalRequestId = (
            (item as any).parsedExternalRequestId
            || (item as any).ParsedExternalRequestId
            || ''
          ).toString().trim()
          const subject = ((item as any).subject || (item as any).Subject || '').toString().trim()
          const workerName = (item.workerFullName || '').toString().trim()
          const label = externalRequestId || subject || workerName || 'Request'
          callbacks.onOpenLinkedRequest(requestId, label)
        } else {
          console.warn('open_linked_request: no linkedVmsRequestId on row', item)
        }
      }
      break

    default:
      console.warn('Unknown action type:', action.actionType, '(normalized:', actionType, ')')
  }
}
